import { Route, Routes } from 'react-router';

import { WorkflowScopeType } from '@invenco/common-domain/enums';

import { Dashboard } from '@/pages/seller/dashboard/dashboard';
import { InventoryQuantities } from '@/pages/seller/inventory/quantities/inventory-quantities';
import { SkuList } from '@/pages/seller/product/sku-list/sku-list';
import { OrderList } from '@/pages/seller/sales/orders/order-list/order-list';
import { commonRoutes, redirectRoute } from '@/shared/helpers/routing';
import { useAuthToken } from '@/shared/hooks/useAuthToken';
import { useFeatureFlags } from '@/shared/providers/FeatureFlagProvider';

import { WorkflowDetails } from '../common/workflows/workflow-details/WorkflowDetails';
import { WorkflowEditor } from '../common/workflows/workflow-editor/workflow-editor';
import { WorkflowList } from '../common/workflows/workflow-list/workflow-list';
import { InsightDetails } from './analytics/insight-details/insight-details';
import { InsightList } from './analytics/insight-list/insight-list';
import { SkubanaInstall } from './integration/install/SkubanaInstall';
import { SkuDetails } from './product/sku-details/sku-details';
import { ChannelDetails } from './sales/channels/channel-details/ChannelDetails';
import { ChannelList } from './sales/channels/channel-list/channel-list';
import { ListingList } from './sales/listings/listing-list/listing-list';
import { OrderDetailsShad } from './sales/orders/order-details-shad/order-details-shad';
import { OrderDetails } from './sales/orders/order-details/OrderDetails';
import { OrderWizard } from './sales/orders/order-wizard/order-wizard';
import { SettingsContainer } from './settings';
import { ShippingProfile } from './settings/shipping-profile/ShippingProfile';
import { ShippingSettings } from './settings/shipping/ShippingSettings';
import { AsnDetails } from './supply/asns/asn-details/asn-details';
import { AsnList } from './supply/asns/asn-list/asn-list';
import { AsnWizard } from './supply/asns/asn-wizard/asn-wizard';
import { ReturnDetails } from './supply/returns/return-details/return-details';
import { ReturnsList } from './supply/returns/returns-list/returns-list';

export function SellerRoutes() {
  const { isInvencoUser } = useAuthToken();
  const { getFeatureFlag } = useFeatureFlags();
  const isUsingNewDashboard = getFeatureFlag('use-new-dashboard');

  return (
    <Routes>
      {redirectRoute('/', '/sales/orders')}
      {redirectRoute('/dashboard', isUsingNewDashboard ? '/new-dashboard' : '/sales/orders')}

      <Route path="/new-dashboard" element={<Dashboard />} />

      <Route path="/products">
        {redirectRoute('', 'skus')}
        <Route path="skus">
          <Route index element={<SkuList />} />
          <Route path=":id" element={<SkuDetails />} />
        </Route>
      </Route>
      <Route path="/supply">
        {redirectRoute('', 'asns')}
        <Route path="asns">
          <Route index element={<AsnList />} />
          <Route path="new" element={<AsnWizard />} />
          <Route path=":id" element={<AsnDetails />} />
        </Route>
        <Route path="returns">
          <Route index element={<ReturnsList />} />
          <Route path=":id" element={<ReturnDetails />} />
        </Route>
      </Route>

      <Route path="/inventory">
        {redirectRoute('', 'quantities')}
        <Route path="quantities" element={<InventoryQuantities />} />
      </Route>

      <Route path="/sales">
        {redirectRoute('', 'orders')}
        <Route path="orders">
          <Route index element={<OrderList />} />
          <Route path=":id" element={<OrderDetails />} />
        </Route>
        <Route path="orders-shad/new" element={<OrderWizard />} />
        <Route path="orders-shad/:id" element={<OrderDetailsShad />} />
        <Route path="channels">
          <Route index element={<ChannelList />} />
          <Route path=":id" element={<ChannelDetails />} />
        </Route>
        <Route path="listings">
          <Route index element={<ListingList />} />
        </Route>
      </Route>

      <Route path="/analytics">
        {redirectRoute('', 'insights')}
        <Route path="insights">
          <Route index element={<InsightList />} />
          <Route path=":id" element={<InsightDetails />} />
        </Route>
      </Route>

      <Route path="/automation">
        {redirectRoute('', 'workflows')}
        <Route path="workflows">
          <Route index element={<WorkflowList scopeType={WorkflowScopeType.ACCOUNT} />} />
          <Route path=":id" element={<WorkflowDetails scopeType={WorkflowScopeType.ACCOUNT} />} />
        </Route>
        <Route
          path="workflows-shad/new"
          element={<WorkflowEditor scopeType={WorkflowScopeType.ACCOUNT} />}
        />
        <Route
          path="workflows-shad/:id"
          element={<WorkflowEditor scopeType={WorkflowScopeType.ACCOUNT} />}
        />
      </Route>

      <Route path="/integration">
        <Route path="app/skubana/install" element={<SkubanaInstall />} />
      </Route>

      <Route path="/settings" element={<SettingsContainer />}>
        {redirectRoute('', 'shipping')}
        <Route path="shipping" element={<ShippingSettings />} />
      </Route>
      {/* Pages which don't use the settings subnav must appear outside the /settings route */}
      <Route path="/settings/shipping/profile/:id" element={<ShippingProfile />} />

      {commonRoutes(isInvencoUser)}
    </Routes>
  );
}
